<template>
  <div>
    <div class="showpilot-cursor" ref="cursor">
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 24 26">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.69 2.69a2.357 2.357 0 0 1 2.495-.54L21.47 8.632a2.357 2.357 0 0 1-.255 4.494l-7.271 1.818-1.818 7.27a2.357 2.357 0 0 1-4.494.256L1.15 5.185a2.357 2.357 0 0 1 .54-2.495Z" fill="#fff" />
        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.633 3.622A.786.786 0 0 0 2.62 4.633L9.103 21.92a.786.786 0 0 0 1.498-.086l2.047-8.185 8.185-2.046a.785.785 0 0 0 .086-1.498L3.633 3.622Z" fill="var(--sp-chat-primary)" />
      </svg>
    </div>
    <div class="selection-rectangle" ref="selectionRectangle"></div>
    <div class="showpilot-cursor-overlay"></div>
  </div>
</template>

<script>
import Typewriter from "../../agent/components/Typewriter/index.vue";

export default {
  name: "AgentChatCursor",
  components: {
    Typewriter,
  },
  data() {
    return {
      startPosition: { x: 0, y: 0 },
      endPosition: { x: 0, y: 0 },
      message: null,
    };
  },
  mounted() {},
  methods: {
    animateCursor(options) {
      let { start, end, action, target = null, duration: customDuration = null, signal = null } = options;

      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const cursorWidth = 28;
      const cursorHeight = 28;

      const rightBoundPadding = 50;
      const viewportBounds = {
        minX: scrollX + rightBoundPadding,
        maxX: scrollX + window.innerWidth - cursorWidth - rightBoundPadding,
        minY: scrollY,
        maxY: scrollY + window.innerHeight - cursorHeight,
      };

      const clampToViewport = (pos) => {
        return {
          x: Math.max(viewportBounds.minX, Math.min(viewportBounds.maxX, pos.x)),
          y: Math.max(viewportBounds.minY, Math.min(viewportBounds.maxY, pos.y)),
        };
      };

      const startAbsolute = clampToViewport({
        x: start.x + scrollX,
        y: start.y + scrollY,
      });

      const endAbsolute = clampToViewport({
        x: end.x + scrollX,
        y: end.y + scrollY,
      });

      this.$refs.cursor.style.left = `${startAbsolute.x}px`;
      this.$refs.cursor.style.top = `${startAbsolute.y}px`;

      const duration = customDuration || 1500 + Math.random() * 1000;
      const fps = 60;
      const totalFrames = duration / (1000 / fps);
      let currentFrame = 0;
      let animationFrameId = null;

      const numControlPoints = 2 + Math.floor(Math.random() * 2);
      const controlPoints = [startAbsolute];

      for (let index = 0; index < numControlPoints; index++) {
        controlPoints.push({
          x: startAbsolute.x + (Math.random() * 0.8 + 0.1) * (endAbsolute.x - startAbsolute.x),
          y: startAbsolute.y + (Math.random() * 0.8 + 0.1) * (endAbsolute.y - startAbsolute.y),
        });
      }

      controlPoints.push(endAbsolute);

      const animate = (resolve, reject) => {
        if (signal && signal.aborted) {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          reject(new Error("Aborted"));
          return;
        }

        if (currentFrame >= totalFrames) {
          if (action === "hover" && target) {
            this.drawSelectionRectangle(endAbsolute, target, signal)
              .then((options) => {
                const { endPosition } = options || {};

                resolve({
                  endPosition,
                });
              })
              .catch((err) => {
                reject(err);
              });
          } else {
            performAction(action);
            resolve();
          }
          return;
        }

        const progress = easeInOutCubic(currentFrame / totalFrames);
        const position = getSplinePoint(controlPoints, progress);
        const clampedPosition = clampToViewport(position);

        this.$refs.cursor.style.left = `${clampedPosition.x}px`;
        this.$refs.cursor.style.top = `${clampedPosition.y}px`;

        const shake = Math.sin(currentFrame * 0.5) * 0.5;
        this.$refs.cursor.style.transform = `translate(${shake}px, ${shake}px)`;

        currentFrame++;
        animationFrameId = requestAnimationFrame(() => animate(resolve, reject));
      };

      function easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }

      function getSplinePoint(points, t) {
        const n = points.length - 1;
        let x = 0;
        let y = 0;

        for (let i = 0; i <= n; i++) {
          const coefficient = binomialCoefficient(n, i) * Math.pow(1 - t, n - i) * Math.pow(t, i);
          x += coefficient * points[i].x;
          y += coefficient * points[i].y;
        }

        return { x, y };
      }

      function binomialCoefficient(n, k) {
        let result = 1;
        for (let i = 1; i <= k; i++) {
          result *= (n - i + 1) / i;
        }
        return result;
      }

      function performAction(action) {
        if (!target) return;

        const clickAnimation = [
          { transform: "scale(1)", opacity: 1 },
          { transform: "scale(0.95)", opacity: 0.8 },
          { transform: "scale(1)", opacity: 1 },
        ];

        const animationTiming = {
          duration: 1000,
          iterations: 2,
          easing: "ease-in-out",
        };

        if (action === "click") {
          target.animate(clickAnimation, animationTiming);
        }
      }

      return new Promise((resolve, reject) => {
        if (signal && signal.aborted) {
          reject(new Error("Aborted"));
          return;
        }

        if (signal) {
          signal.addEventListener("abort", () => {
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            reject(new Error("Aborted"));
          });
        }

        animate(resolve, reject);
      });
    },
    drawSelectionRectangle(startPoint, target, signal = null) {
      if (!target || !this.$refs.selectionRectangle || !this.$refs.cursor) {
        return Promise.resolve();
      }

      const rect = target.getBoundingClientRect();
      const selectionRect = this.$refs.selectionRectangle;
      const cursor = this.$refs.cursor;

      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const cursorWidth = 28;
      const cursorHeight = 28;

      const rightBoundPadding = 50;
      const viewportBounds = {
        minX: scrollX + rightBoundPadding,
        maxX: scrollX + window.innerWidth - cursorWidth - rightBoundPadding,
        minY: scrollY,
        maxY: scrollY + window.innerHeight - cursorHeight,
      };

      const clampToViewport = (pos) => {
        return {
          x: Math.max(viewportBounds.minX, Math.min(viewportBounds.maxX, pos.x)),
          y: Math.max(viewportBounds.minY, Math.min(viewportBounds.maxY, pos.y)),
        };
      };

      const padding = 10;
      const startX = startPoint.x;
      const startY = startPoint.y;

      const widthVariation = (Math.random() - 0.5) * 6;
      const heightVariation = (Math.random() - 0.5) * 6;

      let finalLeft = rect.left + scrollX - padding;
      let finalTop = rect.top + scrollY - padding;
      let finalWidth = rect.width + padding * 2 + widthVariation;
      let finalHeight = rect.height + padding * 2 + heightVariation;

      finalLeft = Math.max(viewportBounds.minX, finalLeft);
      finalTop = Math.max(viewportBounds.minY, finalTop);

      const maxRight = Math.min(viewportBounds.maxX, finalLeft + finalWidth);
      const maxBottom = Math.min(viewportBounds.maxY, finalTop + finalHeight);

      finalWidth = Math.max(0, maxRight - finalLeft);
      finalHeight = Math.max(0, maxBottom - finalTop);

      const finalRight = finalLeft + finalWidth;
      const finalBottom = finalTop + finalHeight;

      const clampedFinalRight = clampToViewport({ x: finalRight, y: finalBottom });

      const duration = 2000;
      const fps = 60;
      const totalFrames = duration / (1000 / fps);
      let currentFrame = 0;
      let animationFrameId = null;

      selectionRect.style.display = "block";
      selectionRect.style.left = `${finalLeft}px`;
      selectionRect.style.top = `${finalTop}px`;
      selectionRect.style.width = "0px";
      selectionRect.style.height = "0px";
      selectionRect.style.border = "3px solid #1255fe";
      selectionRect.style.borderRadius = "4px";

      function easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
      }

      const animate = (resolve, reject) => {
        if (signal && signal.aborted) {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          selectionRect.style.display = "none";
          cursor.style.transform = "";
          reject(new Error("Aborted"));
          return;
        }

        if (currentFrame >= totalFrames) {
          cursor.style.left = `${clampedFinalRight.x}px`;
          cursor.style.top = `${clampedFinalRight.y}px`;
          cursor.style.transform = "";
          selectionRect.style.left = `${finalLeft}px`;
          selectionRect.style.top = `${finalTop}px`;
          selectionRect.style.width = `${finalWidth}px`;
          selectionRect.style.height = `${finalHeight}px`;

          setTimeout(() => {
            selectionRect.style.display = "none";
            resolve({
              endPosition: {
                x: clampedFinalRight.x,
                y: clampedFinalRight.y,
              },
            });
          }, 2500);

          return;
        }

        const progress = easeOutCubic(currentFrame / totalFrames);

        const currentCursorX = startX + (finalRight - startX) * progress;
        const currentCursorY = startY + (finalBottom - startY) * progress;
        const clampedCursor = clampToViewport({ x: currentCursorX, y: currentCursorY });

        cursor.style.left = `${clampedCursor.x}px`;
        cursor.style.top = `${clampedCursor.y}px`;
        cursor.style.transform = "";

        let currentWidth = Math.max(0, currentCursorX - finalLeft);
        let currentHeight = Math.max(0, currentCursorY - finalTop);

        const maxCurrentRight = Math.min(viewportBounds.maxX, finalLeft + currentWidth);
        const maxCurrentBottom = Math.min(viewportBounds.maxY, finalTop + currentHeight);

        currentWidth = Math.max(0, maxCurrentRight - finalLeft);
        currentHeight = Math.max(0, maxCurrentBottom - finalTop);

        selectionRect.style.left = `${finalLeft}px`;
        selectionRect.style.top = `${finalTop}px`;
        selectionRect.style.width = `${currentWidth}px`;
        selectionRect.style.height = `${currentHeight}px`;

        currentFrame++;
        animationFrameId = requestAnimationFrame(() => animate(resolve, reject));
      };

      return new Promise((resolve, reject) => {
        if (signal && signal.aborted) {
          selectionRect.style.display = "none";
          reject(new Error("Aborted"));
          return;
        }

        if (signal) {
          signal.addEventListener("abort", () => {
            if (animationFrameId) {
              cancelAnimationFrame(animationFrameId);
            }
            selectionRect.style.display = "none";
            reject(new Error("Aborted"));
          });
        }

        animate(resolve, reject);
      });
    },
    showMessage(message) {
      this.message = message;
    },
    hideMessage() {
      this.message = null;
    },
  },
};
</script>

<style scoped>
.showpilot-cursor {
  position: absolute;
  width: 28px;
  height: 28px;
  pointer-events: none;
  z-index: 9998;
  top: 0;

  .showpilot-cursor-message {
    background: #f3f7ff;
    border: 2px solid #f5f5f5;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    width: max-content;
    max-width: 200px;
    margin-left: 12px;
    border-radius: 5px;
    padding: 10px;
    line-height: 2;
  }
}

.selection-rectangle {
  position: absolute;
  border: 2px solid var(--sp-chat-primary);
  background: #0b33981a;
  pointer-events: none;
  z-index: 9998;
  display: none;
  box-sizing: border-box;
}

.showpilot-cursor-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 9997;
  
  animation: box-shadow-pulse 2s ease-in-out infinite;
}

@keyframes box-shadow-pulse {
  0%, 100% {
    box-shadow:
      inset 0 0 12px 5px rgba(0, 0, 0, 0.35),
      inset 0 0 22px 7px rgba(var(--sp-chat-primary-rgb), 0.33),
      inset 0 0 30px 10px rgba(var(--sp-chat-primary-rgb), 0.15);
  }
  50% {
    box-shadow:
      inset 0 0 20px 10px rgba(0, 0, 0, 0.38),
      inset 0 0 32px 12px rgba(var(--sp-chat-primary-rgb), 0.36),
      inset 0 0 40px 15px rgba(var(--sp-chat-primary-rgb), 0.22);
  }
}
</style>
